import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Input } from "ui-kit/PrimitiveControls/Input";
import { Button } from "ui-kit/Button";
import { Routes } from "constants/routes";
import Edit from "assets/edit.svg";
import BackArrow from "assets/back-arrow.svg";
import { Toast } from "primereact/toast";
import { PreviewImage } from "./PreviewImage";
import {FormattedMessage} from "react-intl";
import IntlStore from "stories/IntlStore";

interface FormProps {
  id: string;
  name: string;
  description: string;
  releasesCount: number;
  src?: string;
  updating: boolean;
  onDelete: () => void;
  onSave: (data: any) => Promise<void>;
}

export const Form: React.FC<FormProps> = props => {
  const { name, description, releasesCount, updating, src, onDelete, onSave } = props;
  const navigate = useNavigate();
  const [inputName, setInputName] = useState(name);
  const [inputDescription, setInputDescription] = useState(description);
  const [isEditingName, setEditingName] = useState(false);
  const [isEditingDescription, setEditingDescription] = useState(false);
  const toast = useRef<any>(null);

  useEffect(() => {
    setInputName(name);
    setInputDescription(description);
  }, [name, description]);

  const onSaveName = async () => {
    try {
      await onSave({ name: inputName });
      setEditingName(false);
      toast.current?.show({
        severity: 'success',
        summary: IntlStore.intl.formatMessage({
          id: "Labels.Success",
          defaultMessage: "Success"
        }),
        detail: IntlStore.intl.formatMessage({
          id: "Labels.NewNameSavedSuccessfully",
          defaultMessage: "New name saved successfully"
        }),
      });
    } catch (e: any) {
      toast.current?.show({
        severity: 'error',
        summary: IntlStore.intl.formatMessage({
          id: "Labels.Error",
          defaultMessage: "Error"
        }),
        detail: e.message
      });
    }
  }

  const onSaveDescription = async () => {
    try {
      await onSave({ description: inputDescription });
      setEditingDescription(false);
      toast.current?.show({
        severity: 'success',
        summary: IntlStore.intl.formatMessage({
          id: "Labels.Success",
          defaultMessage: "Success"
        }),
        detail: IntlStore.intl.formatMessage({
          id: "Labels.NewDescriptionSavedSuccessfully",
          defaultMessage: "New description saved successfully"
        }),
      });
    } catch (e: any) {
      toast.current?.show({
        severity: 'error',
        summary: IntlStore.intl.formatMessage({
          id: "Labels.Error",
          defaultMessage: "Error"
        }),
        detail: e.message
      });
    }
  }

  return (
    <Container>
      <Breadcrumbs>
        <Breadcrumb onClick={() => navigate(Routes.Labels)}>Labels</Breadcrumb>
        <BackArrow color="rgba(0, 0, 0, 0.5)" height="15px" />
        <Breadcrumb $isActive={true}>{name}</Breadcrumb>
      </Breadcrumbs>
      <Info>
        <PreviewImage src={src} onSave={onSave} />
        <Right>
          <Sections>
            {isEditingName ? (
              <EditNameContainer>
                <Input name="name" value={inputName} onChange={(e) => setInputName(e.target.value)} />
                <SaveButton onClick={onSaveName} loading={updating} disabled={inputName.length === 0 || inputName === name}>
                  <FormattedMessage
                    id="Labels.Form.Save"
                    defaultMessage="Save"
                  />
                </SaveButton>
              </EditNameContainer>
            ) : (
              <InfoName>
                {name || '-'}
                <EditButton onClick={() => setEditingName(true)}>
                  <Edit />
                </EditButton>
              </InfoName>
            )}
            <DeleteButton onClick={onDelete}>
              <FormattedMessage
                id="Labels.Form.DeleteLabel"
                defaultMessage="Delete label"
              />
            </DeleteButton>
          </Sections>
          <InfoReleases>
            <FormattedMessage
              id="Labels.Form.ReleasesCount"
              defaultMessage="{releasesCount} releases"
              values={{ releasesCount }}
            />
          </InfoReleases>
          {isEditingDescription ? (
            <EditDescriptionContainer>
              <Input name="description" value={inputDescription} onChange={(e) => setInputDescription(e.target.value)} />
              <SaveButton onClick={onSaveDescription} loading={updating} disabled={inputDescription.length === 0 || inputDescription === description}>
                <FormattedMessage
                  id="Labels.Form.Save"
                  defaultMessage="Save"
                />
              </SaveButton>
            </EditDescriptionContainer>
          ) : (
            <InfoDescription>
              {description || '-'}
              <EditButton onClick={() => setEditingDescription(true)}>
                <Edit />
              </EditButton>
            </InfoDescription>
          )}
        </Right>
      </Info>
      <Toast ref={toast} />
    </Container>
  );
};


const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  border-radius: 30px;
  background-color: #ffffff;
  padding: 30px 20px 20px;
`;

const Sections = styled.div`
  display: flex;
  justify-content: space-between;
`;

const Info = styled.div`
  display: flex;
  gap: 10px;
`;

const InfoName = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  width: fit-content;
  border: 1px solid #A4A4A4;
  border-radius: 8px;
  padding: 10px 32px;
  font-weight: 700;
  font-size: 16px;
  line-height: 16px;
`;

const InfoDescription = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  width: fit-content;
  border: 1px solid #A4A4A4;
  border-radius: 8px;
  padding: 8px 10px;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: rgba(0, 0, 0, 0.5);
`;

const EditButton = styled.div`
  cursor: pointer;
`;

const Breadcrumbs = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
`;

const Breadcrumb = styled.div<{ $isActive?: boolean }>`
  font-weight: 400;
  font-size: 15px;
  line-height: 20px;
  color: rgba(0, 0, 0, 0.5);
  cursor: ${props => props.$isActive ? 'default' : 'pointer'};
`;

const InfoReleases = styled.div`
  font-weight: 600;
  font-size: 14px;
  line-height: 18px;
  color: rgba(0, 0, 0, 0.5);
`;

const DeleteButton = styled.div`
  padding: 4px 18px;
  border: 1px solid #A4A4A4;
  border-radius: 100px;
  color: #A4A4A4;
  height: 24px;
  cursor: pointer;
  font-weight: 500;
  font-size: 13px;
  line-height: 16px;
`;

const EditNameContainer = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 5px;
`;

const EditDescriptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 10px;
  width: 100%;
`;

const SaveButton = styled(Button)`
  width: 60px;
`;

const Right = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
`;
